import 'regenerator-runtime/runtime';
import './index.css';

import * as Flex from '@twilio/flex-ui';

import { LOG_LEVEL, getConfig, setAppHeight } from '@amc-technology/davinci-api';

import ReactDOM from 'react-dom';
import { AMCRoot } from './AMCRoot';
import App from './App';
import AppFlex from './AppFlex';
import registerServiceWorker from './registerServiceWorker';
import LoggerService from './Services/loggerService';

const mountNode = document.getElementById('root');
let isAppInitialized = false;
let useAMCUI = true;
let logger;

window.onload = async () => {
  const fname = 'indexOnLoad';
  try {
    const serverConfig = await (await fetch('ClientConfiguration')).json();

    if (window.top !== window.self) {
      // This code executes when Twilio Flex App get loaded with in DaVinci UI as an App
      const daVinciAppConfig = await getConfig();
      const appConfig = { daVinciAppConfig, serverConfig };

      localStorage.setItem('LocalLog', daVinciAppConfig.variables.LocalLog?.toString() ?? 'false');

      logger = new LoggerService();
      await logger.initializeLogger();

      const PageUnloadLogs = daVinciAppConfig.variables.PageUnloadLogs ?? false;
      if (PageUnloadLogs) {
        setupNavigationLogging();
        setupActivityHeartbeatLogging();
        logger.log(LOG_LEVEL.Debug, 'PageUnloadLogs', 'Unload logging is enabled');
        await logger.logger.pushLogsAsync();

        const pageUnloadLog = async (eventName) => {
          try {
            const timestamp = new Date().toISOString();
            console.log(`Window event trigger: ${eventName} at ${timestamp}`);
            logger.log(LOG_LEVEL.Debug, 'PageUnloadLogs', `Window event trigger: ${eventName} at ${timestamp}`);
            await logger.logger.pushLogsAsync();
          } catch (error) {
            console.log('Error logging unload event:', error);
            logger.log(LOG_LEVEL.Error, 'PageUnloadLogs', `Error logging ${eventName} event`, error);
            await logger.logger.pushLogsAsync();
          }
        };

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            pageUnloadLog('visibilitychange');
          }
        });
        window.addEventListener('pagehide', (event) => {
          const isPersisted = `persisted=${event.persisted}`;
          pageUnloadLog(`pagehide (${isPersisted})`);
        });
        window.addEventListener('beforeunload', () => pageUnloadLog('beforeunload'));
        window.addEventListener('unload', () => pageUnloadLog('unload'));
      }

      useAMCUI = daVinciAppConfig.variables && daVinciAppConfig.variables.UseAMCUI === true;
      if (!useAMCUI) {
        const flexUIAppHeight = daVinciAppConfig.variables.flexUIAppHeight || 400;
        mountNode.style.setProperty('height', `${flexUIAppHeight}px`);
        setAppHeight(flexUIAppHeight);
      }

      const serviceBaseUrl = `https://${daVinciAppConfig.variables.RuntimeDomain}/`;

      chromeCheck(daVinciAppConfig);

      const accountSid = daVinciAppConfig.variables.AccountSID;

      const response = await fetch(`https://services.twilio.com/v1/Flex/Authentication/Config?AccountSid=${accountSid}`);
      const ssoConfigs = (await response.json()).config_list[0];

      const clientId = ssoConfigs.client_id;
      const connection = ssoConfigs.connection_name;

      const config = {
        serviceBaseUrl: serviceBaseUrl,
        accountSid: accountSid,
        sso: {
          accountSid: accountSid,
          loginPopup: true,
        },
        oauth: {
          clientId: clientId,
          connection: connection,
          redirectUrl: `${window.location.origin}/?AccountSid=${accountSid}&clientId=${clientId}&connection=${connection}`,
        },
        componentProps: {
          AgentDesktopView: {
            showPanel2: false,
          },
        },
      };


      const beforeLoginPopUp = Date.now();
      logger.log(LOG_LEVEL.Debug, fname, `Opening login popup at ${beforeLoginPopUp}`);

      await Flex.progress(mountNode).provideLoginInfo(config, mountNode);
      const manager = await Flex.Manager.create(config);

      const afterLoginPupUp = Date.now();
      logger.log(LOG_LEVEL.Debug, fname, `Closed login popup at ${afterLoginPupUp}. ${afterLoginPupUp - beforeLoginPopUp} `);

      renderApp(manager, appConfig);
    } else {
      // This code only executes on SSO POPUP Redirect to the Twilio Flex URL
      // Upon authentication, URL gets redirected back to DaVinci Flex App URL
      // and below default code detects the same and does the necessary to notify

      const urlParams = new URLSearchParams(window.location.search);

      let accountSid = urlParams.get('AccountSid');
      if (accountSid) {
        localStorage.setItem('accountSid', accountSid);
      } else {
        accountSid = localStorage.getItem('accountSid');
      }

      let clientId = urlParams.get('clientId');
      if (clientId) {
        localStorage.setItem('clientId', clientId);
      } else {
        clientId = localStorage.getItem('clientId');
      }

      let connection = urlParams.get('connection');
      if (connection) {
        localStorage.setItem('connection', connection);
      } else {
        connection = localStorage.getItem('connection');
      }

      const config = {
        accountSid: accountSid,
        sso: {
          accountSid: accountSid,
          loginPopup: false,
        },
        oauth: {
          clientId: clientId,
          connection: connection,
          redirectUrl: window.location.origin,
        },
      };

      await Flex.progress(mountNode).provideLoginInfo(config, mountNode);
      const manager = await Flex.Manager.create(config);
      renderApp(manager);
    }
  } catch (error) {
    logger.log(LOG_LEVEL.Error, fname, 'Failed to initialize Flex App', error);
  }
};

function renderApp(manager, appConfig) {
  const fname = 'renderApp';
  try {
    logger.log(LOG_LEVEL.Debug, fname, 'Rendering app', { manager, appConfig });
    if (!useAMCUI) {
      if (!isAppInitialized) {
        isAppInitialized = true;
        new AMCRoot({ manager: manager, config: appConfig, logger: logger });
      }

      ReactDOM.render(<AppFlex manager={manager} />, mountNode);
    } else {
      ReactDOM.render(<App manager={manager} config={appConfig} logger={logger} />, mountNode);
    }
  } catch (error) {
    logger.log(LOG_LEVEL.Error, fname, 'Render app failed', error);
  }
}

function generateSoundElement(soundUrl) {
  var audio = document.createElement('audio');
  audio.src = soundUrl;
  audio.id = 'AudioHTML';
  audio.type = 'audio/mpeg';
  audio.addEventListener(
    'ended',
    function () {
      this.currentTime = 0;
      this.play();
    },
    false,
  );
  document.body.appendChild(audio);
}

function userSelectedYesSoundNotifications() {
  localStorage.setItem('SoundNotifications', true);

  if (useAMCUI) {
    setAppHeight(10);
  }

  var element = document.getElementById('promptUserForSoundDiv');
  element.parentNode.removeChild(element);
  var spacingElement = document.getElementById('spaceDiv');
  spacingElement.parentNode.removeChild(spacingElement);
}

function chromeCheck(daVinciAppConfig) {
  try {
    const configuredNotificationSound = daVinciAppConfig.variables.AlertingInteractionSound;
    localStorage.setItem('SoundIsConfigured', configuredNotificationSound.length > 0);
    generateSoundElement(String(configuredNotificationSound));

    var isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome && configuredNotificationSound.length > 0) {
      localStorage.setItem('isChrome', true);
      localStorage.setItem('SoundNotifications', false);

      //Construct a div for the user to click on yes/no for sound notifications
      var promptUserDiv = document.createElement('div');
      promptUserDiv.id = 'promptUserForSoundDiv';
      promptUserDiv.style = 'background-color: #F4F5FB; height: 24.6px;';

      if (useAMCUI) {
        document.body.appendChild(promptUserDiv);
      } else {
        document.body.prepend(promptUserDiv);
      }

      var promptLabel = document.createElement('label');
      promptLabel.id = 'promptLabelID';
      promptLabel.style = 'margin-left: 10px;';
      promptUserDiv.appendChild(promptLabel);

      var labelText = document.createElement('b');
      labelText.textContent = 'Sound Notifications';
      labelText.style = 'font-size: 0.8em; font-weight: 700; font-family: Arial; color: #333; ';
      promptLabel.appendChild(labelText);

      var yesButton = document.createElement('button');
      yesButton.textContent = 'Allow';
      yesButton.style = 'margin-left: 10px; background-color: #1e72ba; border: 0; color: white; height: 70%; width: 60px; font-size: 70%;';
      yesButton.onclick = userSelectedYesSoundNotifications;
      promptLabel.parentNode.insertBefore(yesButton, promptLabel.nextSibling);

      var spaceDiv = document.createElement('div');
      spaceDiv.style = 'height: 5px; background-color: white;';
      spaceDiv.id = 'spaceDiv';
      promptUserDiv.parentNode.insertBefore(spaceDiv, promptUserDiv.nextSibling);
    } else {
      localStorage.setItem('isChrome', false);
    }
  } catch (error) { }
}

function setupNavigationLogging() {
  const fname = 'NavigationLogging';

  try {
    if (!logger || !window.history) {
      return;
    }

    if (window.__amcNavigationLoggingInstalled) {
      return;
    }

    const toOriginPath = (urlValue) => {
      try {
        const parsedUrl = new URL(urlValue, window.location.href);
        return `${parsedUrl.origin}${parsedUrl.pathname}`;
      } catch (error) {
        return undefined;
      }
    };

    // Wrap history.pushState
    const originalPushState = window.history.pushState;
    window.history.pushState = function (state, title, url) {
      const fromUrl = toOriginPath(window.location.href);
      const result = originalPushState.apply(this, arguments);
      const toUrl = toOriginPath(window.location.href);

      logger.log(LOG_LEVEL.Debug, fname, 'history.pushState invoked', {
        fromUrl,
        toUrl,
        title,
        requestedUrl: url == null || url === '' ? undefined : toOriginPath(url),
        hasState: state !== null && typeof state !== 'undefined',
      }).catch((error) => {
        console.log('Error logging history.pushState event:', error);
      });

      return result;
    };

    // Wrap history.replaceState
    const originalReplaceState = window.history.replaceState;
    window.history.replaceState = function (state, title, url) {
      const fromUrl = toOriginPath(window.location.href);
      const result = originalReplaceState.apply(this, arguments);
      const toUrl = toOriginPath(window.location.href);

      logger.log(LOG_LEVEL.Debug, fname, 'history.replaceState invoked', {
        fromUrl,
        toUrl,
        title,
        requestedUrl: url == null || url === '' ? undefined : toOriginPath(url),
        hasState: state !== null && typeof state !== 'undefined',
      }).catch((error) => {
        console.log('Error logging history.replaceState event:', error);
      });

      return result;
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', (event) => {
      logger.log(LOG_LEVEL.Debug, fname, 'popstate event fired', {
        url: toOriginPath(window.location.href),
        hasState: event.state !== null && typeof event.state !== 'undefined',
      }).catch((error) => {
        console.log('Error logging popstate event:', error);
      });
    });

    window.__amcNavigationLoggingInstalled = true;
    logger.log(LOG_LEVEL.Debug, fname, 'Navigation logging is enabled (pushState, replaceState, popstate)');
  } catch (error) {
    if (logger) {
      logger.log(LOG_LEVEL.Error, fname, 'Failed to initialize navigation logging', error);
    }
  }
}

function setupActivityHeartbeatLogging() {
  const fname = 'ActivityHeartbeatLogging';

  try {
    if (!logger) {
      return;
    }

    if (window.__amcActivityHeartbeatInstalled) {
      return;
    }

    const heartbeatIntervalMs = 30 * 1000;

    let lastActivityAt = Date.now();
    let lastActivityEvent = 'init';

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, () => {
        lastActivityAt = Date.now();
        lastActivityEvent = eventName;
      }, { passive: true });
    });

    document.addEventListener('visibilitychange', () => {
      lastActivityAt = Date.now();
      lastActivityEvent = `visibilitychange (${document.visibilityState})`;
    });

    setInterval(() => {
      const now = Date.now();
      const idleForMs = now - lastActivityAt;

      logger.log(LOG_LEVEL.Loop, fname, 'Activity heartbeat', {
        lastActivityEvent,
        lastActivityAt: new Date(lastActivityAt).toISOString(),
        idleForMs,
        currentUrl: window.location.href,
        visibilityState: document.visibilityState,
      });
    }, heartbeatIntervalMs);

    window.__amcActivityHeartbeatInstalled = true;
    logger.log(LOG_LEVEL.Debug, fname, 'Activity heartbeat logging is enabled', {
      heartbeatIntervalMs,
    });
  } catch (error) {
    if (logger) {
      logger.log(LOG_LEVEL.Error, fname, 'Failed to initialize activity heartbeat logging', error);
    }
  }
}

registerServiceWorker();
